import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { AdminApiService } from 'shared/services/api/admin.api.service';
import { APIResponse } from 'fakturnia-shared';
import { Subscription } from 'rxjs';
import { DateTime } from 'luxon';

interface TourOverview {
  totalUsersWithOnboarding: number;
  totalUsersWithoutOnboarding: number;
  totalFeedbacks: number;
  completedCount: number;
  skippedCount: number;
  avgRating: number | null;
  avgStepsCompleted: number;
  avgCompletionPercent: number;
  ratingCount: number;
  ratingDistribution: { [key: number]: number };
  stepsCompletedDistribution: { [step: number]: number };
  avgCompletionDuration: number | null;
  avgSkipDuration: number | null;
}

interface FeedbackEntry {
  _id: string;
  user: { email: string; name: string; surname: string } | null;
  rating: number | null;
  completed: boolean;
  stepsCompleted: number;
  totalSteps: number;
  completionPercent: number;
  durationSeconds: number | null;
  createdAt: string;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatButtonModule,
  ],
  selector: 'app-tour-statistics',
  templateUrl: './tour-statistics.component.html',
  styleUrls: ['./tour-statistics.component.scss']
})
export class TourStatisticsComponent implements OnInit, OnDestroy {
  isLoading = true;
  overview: TourOverview | null = null;
  feedbacks: FeedbackEntry[] = [];
  displayedColumns = ['user', 'status', 'rating', 'steps', 'duration', 'device', 'date', 'actions'];
  Math = Math;

  readonly stepNames: string[] = [
    'Witaj w przewodniku po systemie',
    'Pulpit',
    'Dokumenty',
    'Wyszukiwarka',
    'Widok',
    'Wystaw dokument',
    'Typ dokumentu',
    'Nabywca',
    'Daty i waluta',
    'Adnotacje',
    'Sekcja płatności',
    'Pozycje na dokumencie',
    'Dostosuj dokument',
    'Utwórz dokument',
    'Akcje dokumentu',
    'Wysyłka do KSeF',
    'Historia aktywności',
    'Klienci',
    'Produkty',
    'Menu użytkownika',
    'Opcje konta',
    'Dane personalne',
    'Logo firmy',
    'Konta bankowe',
    'Weryfikacja 2FA',
    'Zalogowane urządzenia',
    'Ustawienia aplikacji',
    'Motyw aplikacji',
    'Numerowanie dokumentów',
    'Typ numerowania',
    'Tryb wprowadzania cen',
    'Dane sprzedawcy',
    'Stawki VAT',
    'Sekcja Inne',
    'Krajowy System e-Faktur',
    'Certyfikat KSeF',
    'Podsumowanie',
    'Ocena'
  ];

  private _sub: Subscription | null = null;

  constructor(private _adminApi: AdminApiService) {}

  ngOnInit(): void {
    this._loadStats();
  }

  ngOnDestroy(): void {
    this._sub?.unsubscribe();
  }

  private _loadStats(): void {
    this.isLoading = true;
    this._sub = this._adminApi.getTourStats().subscribe({
      next: (response: APIResponse) => {
        if (response.success && response.data) {
          this.overview = response.data.overview;
          this.feedbacks = response.data.recentFeedbacks || [];
        }
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  formatDate(dateStr: string): string {
    return DateTime.fromISO(dateStr).toFormat('dd.MM.yyyy HH:mm');
  }

  formatDuration(seconds: number | null): string {
    if (!seconds) return '—';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
  }

  getRatingStars(rating: number | null): string {
    if (!rating) return '—';
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  }

  getMaxRatingCount(): number {
    if (!this.overview?.ratingDistribution) return 1;
    return Math.max(...Object.values(this.overview.ratingDistribution), 1);
  }

  deleteFeedback(id: string): void {
    this._adminApi.deleteTourFeedback(id).subscribe({
      next: (response: APIResponse) => {
        if (response.success) {
          this._loadStats();
        }
      }
    });
  }

  /** Buduje lejek: dla każdego kroku ilu użytkowników dotarło co najmniej do niego */
  getFunnelSteps(): { step: number; name: string; reached: number }[] {
    if (!this.overview?.stepsCompletedDistribution || this.overview.totalFeedbacks === 0) return [];

    const dist = this.overview.stepsCompletedDistribution;
    const total = this.overview.totalFeedbacks;
    const totalSteps = this.stepNames.length;
    const result: { step: number; name: string; reached: number }[] = [];

    // Kumulatywnie od góry: reached[s] = ilu userów ma stepsCompleted >= s
    // Klucz w dist może być > totalSteps-1 (np. 38 dla ukończonych), więc zaczynamy od max klucza
    const maxKey = Math.max(...Object.keys(dist).map(Number), totalSteps - 1);
    let cumulative = 0;
    const reachedMap: number[] = new Array(totalSteps).fill(0);

    for (let s = maxKey; s >= 1; s--) {
      cumulative += (dist[s] || 0);
      if (s < totalSteps) {
        reachedMap[s] = cumulative;
      }
    }

    // Krok 0 — wszyscy zaczęli
    reachedMap[0] = total;

    for (let s = 0; s < totalSteps; s++) {
      result.push({
        step: s,
        name: this.stepNames[s] || `Krok ${s + 1}`,
        reached: reachedMap[s]
      });
    }

    return result;
  }

  getFunnelMax(): number {
    return this.overview?.totalFeedbacks || 1;
  }
}
